import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Radar, Newspaper, BookOpen, BriefcaseBusiness, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { RoleBadge } from "@/components/app/shared";

const ROLE_TEXT = {
  SUPERADMIN: "Visibilidad total: claves de API (Webflow, IA), gestión de usuarios y métricas de negocios.",
  EDITORIAL: "Radar, borradores, calendario editorial, Social Pack, páginas editoriales del Flatplan y envío a Webflow.",
  COMMERCIAL: "Pipeline del CRM, negocios, órdenes de inserción, recepción de artes y asignación de páginas de publicidad en el Flatplan.",
};

const MODULES = [
  { icon: Radar, t: "Radar AI", d: "Monitorea El Salvador, Centroamérica y medios globales. Convierte pitches en historias con un clic." },
  { icon: Newspaper, t: "Mesa Editorial", d: "Calendario mensual, semanal, Kanban y tabla. Del pitch a publicado, con Social Pack." },
  { icon: BookOpen, t: "Flatplan Libro Anual", d: "Paginación del volumen impreso por pliegos y firmas, con checklist de imprenta." },
  { icon: BriefcaseBusiness, t: "CRM Comercial", d: "Pipeline de pauta: de lead a pagado, con checklist de tráfico y producción." },
  { icon: RefreshCw, t: "Webflow", d: "Envía historias al CMS como borrador o publícalas sin duplicados." },
];

export default function Onboarding({ me, onDone }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(me.name || "");
  const [title, setTitle] = useState(me.title || "");
  const [saving, setSaving] = useState(false);
  const steps = ["Bienvenida", "Tu rol", "Perfil", "Guía rápida"];

  const finish = async () => {
    if (!name.trim()) return toast.error("Escribe tu nombre");
    setSaving(true);
    try {
      await api.patch("/me", { name: name.trim(), title: title.trim(), onboarded: true });
      toast.success("Bienvenida completada");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      setSaving(false);
    }
  };

  return (
    <div className="mesh-soft grain flex min-h-screen items-center justify-center px-4 py-10" data-testid="onboarding-screen">
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="font-display text-3xl">Mediana Hub</div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            Paso {step + 1} / {steps.length} · {steps[step]}
          </div>
        </div>
        <Progress value={((step + 1) / steps.length) * 100} className="h-1" />
        <div className="mt-6 min-h-[380px] rounded-xl border border-border bg-card p-8 md:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}>
              {step === 0 && (
                <div>
                  <div className="kicker">Bienvenida</div>
                  <h1 className="mt-4 font-display text-6xl leading-[0.9] tracking-tight md:text-7xl">Hola, {(me.name || "").split(" ")[0] || "equipo"}.</h1>
                  <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-muted-foreground">
                    Este es el sistema operativo interno de Mediana: donde se detectan las historias, se edita, se pagina el libro anual y se gestiona la pauta comercial.
                  </p>
                  <div className="mt-6 font-mono text-xs text-muted-foreground">{me.email}</div>
                </div>
              )}
              {step === 1 && (
                <div>
                  <div className="kicker">Confirmación de rol</div>
                  <h2 className="mt-4 font-display text-5xl tracking-tight">Tu rol asignado</h2>
                  <div className="mt-6 flex items-center gap-3">
                    <RoleBadge role={me.role} className="px-3 py-1 text-xs" />
                    <span className="text-sm text-muted-foreground">asignado automáticamente</span>
                  </div>
                  <p className="mt-5 max-w-lg text-[15px] leading-relaxed" data-testid="onboarding-role-text">{ROLE_TEXT[me.role]}</p>
                  <p className="mt-4 text-xs text-muted-foreground">
                    {me.role === "SUPERADMIN"
                      ? "Puedes cambiar los roles del equipo en Equipo & Ajustes."
                      : `Si necesitas otro rol distinto a ${ROLE_LABEL[me.role]}, pídelo a un Superadmin.`}
                  </p>
                </div>
              )}
              {step === 2 && (
                <div>
                  <div className="kicker">Perfil</div>
                  <h2 className="mt-4 font-display text-5xl tracking-tight">¿Cómo te presentamos?</h2>
                  <div className="mt-6 grid gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="ob-name">Nombre completo</Label>
                      <Input id="ob-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="onboarding-name-input" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="ob-title">Cargo</Label>
                      <Input id="ob-title" placeholder="Ej. Editora de Moda, Director Comercial" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="onboarding-title-input" />
                    </div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div>
                  <div className="kicker">Guía rápida</div>
                  <h2 className="mt-4 font-display text-5xl tracking-tight">Los módulos</h2>
                  <div className="mt-5 divide-y divide-border">
                    {MODULES.map((m, i) => (
                      <div key={m.t} className="flex gap-4 py-3">
                        <span className="font-mono text-[11px] text-muted-foreground">0{i + 1}</span>
                        <m.icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
                        <div>
                          <div className="text-sm font-medium">{m.t}</div>
                          <div className="text-[13px] text-muted-foreground">{m.d}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">
                    Atajo: <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd> / <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px]">Ctrl K</kbd> abre la búsqueda global.
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-5 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back-button">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Atrás
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} data-testid="onboarding-next-button">
              Continuar <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={saving} data-testid="onboarding-finish-button">
              <Check className="mr-1.5 h-4 w-4" /> Entrar al Hub
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
